/**
 * Utility functions for calculating optimal, evenly spaced tick values for D3 chart axes.
 * These functions ensure ticks are distributed evenly across the entire axis domain,
 * preventing overcrowding and maintaining readability.
 */

/**
 * Calculates the optimal number of ticks based on available space and minimum label width/height.
 * @param {number} availableSpace - Available width (for x-axis) or height (for y-axis) in pixels
 * @param {number} minLabelSize - Minimum space needed per label in pixels (default: 60 for x-axis, 30 for y-axis)
 * @returns {number} Maximum number of ticks that can fit in the available space
 */
export const calculateOptimalTickCount = (availableSpace, minLabelSize = 60) => {
  if (availableSpace <= 0 || minLabelSize <= 0) {
    return 5; // Default fallback
  }
  return Math.max(2, Math.floor(availableSpace / minLabelSize));
};

/**
 * Generates evenly spaced tick values from an array or sequence.
 * Ensures first and last values are always included, with remaining ticks evenly distributed.
 * @param {Array} sequence - Array of values (dates, numbers, etc.)
 * @param {number} maxTicks - Maximum number of ticks to generate
 * @returns {Array} Array of evenly spaced tick values
 */
export const generateEvenlySpacedTicks = (sequence, maxTicks) => {
  if (!sequence || sequence.length === 0) {
    return [];
  }

  if (sequence.length <= maxTicks) {
    return [...sequence]; // Return all values if we have fewer than maxTicks
  }

  const tickValues = [];
  const step = (sequence.length - 1) / (maxTicks - 1);

  // Always include first value
  tickValues.push(sequence[0]);

  // Generate evenly spaced ticks between first and last
  for (let i = 1; i < maxTicks - 1; i++) {
    const index = Math.round(i * step);
    tickValues.push(sequence[index]);
  }

  // Always include last value if not already included
  const lastIndex = sequence.length - 1;
  if (tickValues[tickValues.length - 1] !== sequence[lastIndex]) {
    tickValues.push(sequence[lastIndex]);
  }

  return tickValues;
};

/**
 * Generates evenly spaced tick values for time-based axes (x-axis).
 * Works with D3 time sequences and ensures even distribution.
 * @param {Array} dateSequence - Array of Date objects from d3.time* functions
 * @param {number} innerWidth - Available width for the axis in pixels
 * @param {number} minLabelWidth - Minimum width needed per label in pixels (default: 80)
 * @returns {Array} Array of evenly spaced Date objects for ticks
 */
export const getTimeAxisTicks = (dateSequence, innerWidth, minLabelWidth = 80) => {
  if (!dateSequence || dateSequence.length === 0) {
    return [];
  }

  const maxTicks = calculateOptimalTickCount(innerWidth, minLabelWidth);
  return generateEvenlySpacedTicks(dateSequence, maxTicks);
};

/**
 * Calculates optimal tick count for linear/numeric axes (y-axis).
 * Uses D3's built-in tick generation but limits the count based on available space.
 * @param {number} innerHeight - Available height for the axis in pixels
 * @param {number} minLabelHeight - Minimum height needed per label in pixels (default: 40)
 * @returns {number} Optimal number of ticks
 */
export const getLinearAxisTickCount = (innerHeight, minLabelHeight = 40) => {
  return calculateOptimalTickCount(innerHeight, minLabelHeight);
};

/**
 * Generates evenly spaced numeric tick values for linear scales.
 * Useful when you need precise control over tick values.
 * @param {number} min - Minimum value of the domain
 * @param {number} max - Maximum value of the domain
 * @param {number} maxTicks - Maximum number of ticks to generate
 * @returns {Array} Array of evenly spaced numeric values
 */
export const generateEvenlySpacedNumericTicks = (min, max, maxTicks) => {
  if (maxTicks <= 2) {
    return [min, max];
  }

  const tickValues = [];
  const step = (max - min) / (maxTicks - 1);

  for (let i = 0; i < maxTicks; i++) {
    tickValues.push(min + i * step);
  }

  // Ensure max is exactly included (handle floating point precision)
  tickValues[tickValues.length - 1] = max;

  return tickValues;
};

